import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { ShieldCheck } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';

export default function Activation() {
  const { t } = useLanguage();
  const email = sessionStorage.getItem('bfi_activation_email') || '';
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!email) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <p>{t('auth.noActivationSession')}</p>
          <Button onClick={() => navigate('/connexion')} className="mt-4 bg-blue-700">{t('common.login')}</Button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/activate', { email, code });
      if (data.step === 'otp') {
        sessionStorage.setItem('bfi_otp_challenge', data.challenge);
        sessionStorage.setItem('bfi_otp_email', data.masked_email);
        sessionStorage.setItem('bfi_otp_validity', String(data.validity_minutes || 10));
        toast.success(t('auth.accountActivated'));
        navigate('/verification-otp');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('auth.wrongActivationCode'));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.post('/auth/activation/resend', { email });
      toast.success(t('auth.activationCodeResent'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('common.operationError')); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
        <div className="flex items-center gap-2 mb-6"><Logo /></div>
        <h1 className="text-2xl font-bold">{t('auth.activationTitle')}</h1>
        <p className="text-slate-600 mt-1 text-sm">{t('auth.activationDesc')} <span className="font-medium">{email}</span>.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t('auth.activationCode')} data-testid="input-activation-code" required/>
          <Button type="submit" className="w-full bg-blue-700" disabled={loading} data-testid="btn-activation-submit">{loading ? t('common.loading') : t('auth.activate')}</Button>
          <Button type="button" variant="outline" className="w-full" onClick={resend} data-testid="btn-activation-resend">{t('auth.resendActivationCode')}</Button>
        </form>
        <div className="mt-6 flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="w-4 h-4 text-emerald-600"/> {t('auth.activationSecurity')}</div>
      </div>
    </div>
  );
}
